import { useMemo } from 'react';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  VersionDrawer,
  type VersionEntry,
} from '@shared/components/VersionDrawer';
import { articlesApi } from '@api/articles';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';

interface Props {
  open: boolean;
  articleId: string;
  currentBody: string;
  onClose: () => void;
}

export function ArticleVersionsDrawer({
  open,
  articleId,
  currentBody,
  onClose,
}: Props) {
  const { t } = useTranslation(['articles', 'common', 'validation', 'errors']);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['article-versions', articleId],
    queryFn: () => articlesApi.listVersions(articleId),
    enabled: open,
  });

  // VersionDrawer keys versions by `id` (string). Backend exposes a per-article
  // `versionNumber` (int) and the unique `id` (GUID). We use `versionNumber`
  // as the key sent to the restore endpoint but keep `id` for the UI.
  const versions = useMemo<VersionEntry[]>(() => {
    const list = data ?? [];
    // Sort newest first.
    return [...list]
      .sort((a, b) => b.versionNumber - a.versionNumber)
      .map((v, idx) => ({
        id: String(v.versionNumber),
        label: t('versions.label', {
          version: v.versionNumber,
          note: v.changeNote ?? t('versions.defaultNote'),
        }),
        author: v.authorEmail ?? t('versions.systemAuthor'),
        createdAt: dayjs(v.createdAt).format('DD.MM.YYYY HH:mm'),
        snapshot: v.body,
        current: idx === 0,
      }));
  }, [data, t]);

  const restoreMutation = useMutation({
    mutationFn: (versionNumber: number) =>
      articlesApi.restoreVersion(articleId, versionNumber),
    onSuccess: async (_d, versionNumber) => {
      toast.success(t('toast.versionRestored', { version: versionNumber }));
      await qc.invalidateQueries({ queryKey: ['articles', articleId] });
      await qc.invalidateQueries({
        queryKey: ['article-versions', articleId],
      });
      await qc.invalidateQueries({ queryKey: ['articles'] });
      void recordAuditAction({
        action: 'article.version.restore',
        resource: 'article',
        resourceId: articleId,
        summary: t('audit.versionRestore', { version: versionNumber }),
        meta: { versionNumber },
      });
      onClose();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  return (
    <VersionDrawer
      open={open}
      onClose={onClose}
      versions={versions}
      currentContent={currentBody}
      onRestore={async (v) => {
        const num = Number(v.id);
        if (Number.isFinite(num)) await restoreMutation.mutateAsync(num);
      }}
    />
  );
}
